import { all, call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';

import { SELECT_DEALER } from 'constants/ActionTypes';
import { CONVERSATION_TYPES } from 'constants/ChatConstants';
import { PATH } from 'constants/RoutesConstants';
import { waitForApplicationReady } from 'modules/app/sagas/utils';
import { simpleRequestsLoader } from 'modules/app/utils';
import * as appointmentTypes from 'modules/appointments/actionTypes';

import * as actions from 'modules/chat/actions';
import * as dashboardAction from 'modules/chat/actions/dashboard';
import * as actionTypes from 'modules/chat/actionTypes';

import { getMediaListFilters } from 'modules/chat/selectors/mediaList';
import { getRequestParams } from 'modules/chat/selectors/request';
import * as interventionTypes from 'modules/interventions/actionTypes';
import { SOCKET_ACTION } from 'modules/sockets/constants';
import { canAccessTo, getCurrentUserIsManufacturer } from 'modules/users/selectors/currentUser';
import { getSelectedDealerId } from 'selectors/dealerSelectors';

import detailsSagas from './details.saga';
import chatSocketSagas from './socket.saga';

/** @type {Record<string, import("modules/app/types").SimpleRequestConfig>} */
const simpleRequests = {
  [actionTypes.POST_MESSAGE]: {
    notifications: {
      success: {
        id: 'page.fiche.comments.added',
        defaultMessage: 'Message sent',
      },
    },
  },
  [actionTypes.PATCH_CONVERSATION_STATUS]: {
    callbacks: {
      /**
       * @param {string} error
       * @param {Object} meta
       * @param {import("modules/chat/types").PatchConversationStatusAction} parentAction
       * @returns {import("modules/chat/types").PatchConversationStatusFailureAction}
       */
      failure: (error, meta, { payload }) => {
        const { conversationId } = payload;
        return dashboardAction.patchConversationStatusFailure(conversationId, error, meta);
      },
    },
  },
  [actionTypes.POST_CONVERSATION]: {
    notifications: {
      success: {
        id: 'page.fiche.notification.create_conversation.success',
        defaultMessage: 'Conversation created',
      },
      failure: {
        id: 'page.fiche.notification.create_conversation.failure',
        defaultMessage: 'Can’t create the conversation',
      },
    },
  },
};

function* hasAccess() {
  const haveAccess = yield select(state => canAccessTo(state, PATH.CHAT.path));
  const isManufacturer = yield select(getCurrentUserIsManufacturer);
  const dealerId = yield select(getSelectedDealerId);

  return haveAccess && !!dealerId && !isManufacturer;
}

function* fetchConversations({ payload, meta }) {
  try {
    const { request } = payload;
    const params = yield select(getRequestParams);

    const { data } = yield call(restClient.request, { ...request, params: { ...params, ...request.params } });
    const content =
      request.params.types !== CONVERSATION_TYPES.INTERNAL
        ? data.content.filter(({ latestMessage }) => latestMessage)
        : data.content;

    yield put(dashboardAction.fetchConversationsSuccess({ ...data, content }, meta));
  } catch (errorMessage) {
    yield put(dashboardAction.fetchConversationsFailure(errorMessage, meta));
  }
}

function* markAllAsReadSideEffect() {
  yield all([
    put(dashboardAction.clearCustomerConversations()),
    put(dashboardAction.clearInternalConversations()),
    put(dashboardAction.fetchConversations({ types: CONVERSATION_TYPES.EXTERNAL })),
    put(dashboardAction.fetchConversations({ types: CONVERSATION_TYPES.INTERNAL })),
    call(triggerUnreadCounts),
  ]);
}

/**  @param {import('../types').FetchUnreadCountsByTypeAction} action */
function* fetchUnreadCountsByType({ payload, meta }) {
  const isAuthorized = yield call(hasAccess);

  if (isAuthorized) {
    try {
      const { request } = payload;
      const { data } = yield call(restClient.request, request);
      yield put(dashboardAction.fetchUnreadCountsByTypeSuccess(data, meta));
    } catch (errorMessage) {
      yield put(dashboardAction.fetchUnreadCountsByTypeFailure(errorMessage, meta));
    }
  }
}

/** @param {import("../types").SendMessageSuccessAction} action */
function* sendMessageSideEffect({ payload }) {
  const params = yield select(getMediaListFilters);
  const { ids, ...entityParams } = params;
  const visible = Object.keys(params).length > 0;

  const shouldRefresh =
    visible &&
    payload.medias?.length > 0 &&
    ids?.includes(payload.conversationId) &&
    Object.entries(entityParams).every(([field, fieldId]) => payload[field] === fieldId);

  if (shouldRefresh) {
    yield put(actions.fetchConversationAttachments({ ids, ...entityParams }, { source: 'message_sent' }));
  }
}

function* triggerUnreadCounts() {
  const dealerId = yield select(getSelectedDealerId);
  if (dealerId) {
    yield put(dashboardAction.fetchUnreadCountsByType(dealerId));
  }
}

function* getUnreadCount() {
  // Wait for the application to be ready
  yield call(waitForApplicationReady);

  // Trigger the first unread count request
  yield call(triggerUnreadCounts);

  // Unread count triggers
  const unreadCountInit = [
    SELECT_DEALER,
    actionTypes.PATCH_CONVERSATION_STATUS_SUCCESS,
    appointmentTypes.READ_MESSAGES_SUCCESS,
    interventionTypes.READ_INTERVENTION_MESSAGES_SUCCESS,
    SOCKET_ACTION.MESSAGE_SEEN,
  ];
  yield takeLatest(unreadCountInit, triggerUnreadCounts);
}

export default function* chatSagas() {
  // Request
  yield all(simpleRequestsLoader(simpleRequests));

  // Unread messages
  yield fork(getUnreadCount);

  // Global actions
  yield takeEvery(actionTypes.FETCH_CONVERSATIONS, fetchConversations);
  yield takeEvery(actionTypes.POST_MESSAGE_SUCCESS, sendMessageSideEffect);
  yield takeLatest(actionTypes.MARK_ALL_AS_READ_SUCCESS, markAllAsReadSideEffect);
  yield takeLatest(actionTypes.FETCH_UNREAD_COUNTS_BY_TYPE, fetchUnreadCountsByType);

  // Context actions
  yield fork(detailsSagas);
  yield fork(chatSocketSagas);
}
